import { useMemo, useRef, useState } from 'react'
import * as XLSX from 'xlsx'
import { X, Upload, Check, AlertTriangle, Loader2, FileDown, Store, UtensilsCrossed, ShoppingBag } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'
import { useMenuItems, useBulkImportProducts, type ImportProductRow } from '@/hooks/useMenu'
import type { MenuItemStatus, SourceType } from '@/types/app'

interface Props {
  onClose: () => void
}

// Target fields a spreadsheet column can be mapped to. 'skip' ignores the column.
// Mirrors every field on the single-product edit form, grouped the same way.
type TargetField =
  | 'skip' | 'category' | 'name' | 'regional_name' | 'description' | 'upc_code'
  | 'price' | 'wholesale_price' | 'status' | 'source_type'
  | 'available_in_store' | 'available_catering' | 'available_store_supply' | 'is_addon'
  | 'per_piece_price' | 'min_pieces'
  | 'half_tray_price' | 'half_tray_serves' | 'half_tray_pieces'
  | 'medium_tray_price' | 'medium_tray_serves' | 'medium_tray_pieces'
  | 'full_tray_price' | 'full_tray_serves' | 'full_tray_pieces'

const FIELD_GROUPS: { label: string; fields: TargetField[] }[] = [
  { label: '', fields: ['skip'] },
  { label: 'Core', fields: ['category', 'name', 'regional_name', 'description', 'upc_code', 'price', 'wholesale_price', 'status', 'source_type'] },
  { label: 'Availability', fields: ['available_in_store', 'available_catering', 'available_store_supply', 'is_addon'] },
  { label: 'Catering — Half Tray',   fields: ['half_tray_price', 'half_tray_serves', 'half_tray_pieces'] },
  { label: 'Catering — Medium Tray', fields: ['medium_tray_price', 'medium_tray_serves', 'medium_tray_pieces'] },
  { label: 'Catering — Full Tray',   fields: ['full_tray_price', 'full_tray_serves', 'full_tray_pieces'] },
  { label: 'Catering — Per Piece',   fields: ['per_piece_price', 'min_pieces'] },
]

const FIELD_LABELS: Record<TargetField, string> = {
  skip: "Don't import",
  category: 'Category',
  name: 'Item Name',
  regional_name: 'Regional Name',
  description: 'Description',
  upc_code: 'UPC Code',
  price: 'Counter / Store Price',
  wholesale_price: 'Wholesale Price',
  status: 'Status (active/seasonal/eighty_sixed/retired)',
  source_type: 'Source Type (inhouse/procured)',
  available_in_store: 'Available: In-Store (yes/no)',
  available_catering: 'Available: Catering (yes/no)',
  available_store_supply: 'Available: Store Supply (yes/no)',
  is_addon: 'Mark as Accompaniment (yes/no)',
  per_piece_price: 'Per Piece — Price',
  min_pieces: 'Per Piece — Min Pieces',
  half_tray_price: 'Half Tray — Price',
  half_tray_serves: 'Half Tray — Serves',
  half_tray_pieces: 'Half Tray — Approx. Pieces',
  medium_tray_price: 'Medium Tray — Price',
  medium_tray_serves: 'Medium Tray — Serves',
  medium_tray_pieces: 'Medium Tray — Approx. Pieces',
  full_tray_price: 'Full Tray — Price',
  full_tray_serves: 'Full Tray — Serves',
  full_tray_pieces: 'Full Tray — Approx. Pieces',
}

// Best-guess mapping from a spreadsheet header to a target field, used to
// pre-fill the mapping step so most imports need zero manual adjustment.
function guessField(header: string): TargetField {
  const h = header.trim().toLowerCase()
  if (!h) return 'skip'
  if (/categ/.test(h)) return 'category'
  if (/regional|local|native/.test(h)) return 'regional_name'
  if (/descrip/.test(h)) return 'description'
  if (/\bupc\b|barcode/.test(h)) return 'upc_code'
  if (/wholesale/.test(h)) return 'wholesale_price'
  if (/^status/.test(h)) return 'status'
  if (/source\s*type|inhouse|procured/.test(h)) return 'source_type'
  if (/accompan|addon|add-on/.test(h)) return 'is_addon'
  if (/in.?store/.test(h)) return 'available_in_store'
  if (/store\s*supply|wholesale\s*avail/.test(h)) return 'available_store_supply'
  if (/catering\s*(avail)?$/.test(h)) return 'available_catering'
  if (/min.*piece/.test(h)) return 'min_pieces'
  if (/half.*piece|small.*piece/.test(h)) return 'half_tray_pieces'
  if (/medium.*piece/.test(h)) return 'medium_tray_pieces'
  if (/(full|large|big).*piece/.test(h)) return 'full_tray_pieces'
  if (/half.*serve|small.*serve/.test(h)) return 'half_tray_serves'
  if (/medium.*serve/.test(h)) return 'medium_tray_serves'
  if (/(full|large|big).*serve/.test(h)) return 'full_tray_serves'
  if (/per\s*piece|\bpiece\b|\bpc\b|each/.test(h)) return 'per_piece_price'
  if (/small|half/.test(h)) return 'half_tray_price'
  if (/medium|med\b/.test(h)) return 'medium_tray_price'
  if (/large|full|big/.test(h)) return 'full_tray_price'
  if (/^(item|name|product|dish)/.test(h)) return 'name'
  if (/price|rate|cost|\$/.test(h)) return 'price'
  return 'skip'
}

function parseNumber(v: unknown): number | null {
  if (v == null || v === '') return null
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/[^0-9.-]/g, ''))
  return isNaN(n) ? null : n
}

// Blank/unrecognized returns null ("not specified" — let the importer's own
// defaults apply) rather than false, so an empty cell never silently disables
// something.
function parseBoolean(v: unknown): boolean | null {
  if (v == null || v === '') return null
  const s = String(v).trim().toLowerCase()
  if (['yes', 'y', 'true', '1', 'x'].includes(s)) return true
  if (['no', 'n', 'false', '0'].includes(s)) return false
  return null
}

function parseStatus(v: unknown): MenuItemStatus | null {
  const s = String(v ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_')
  if (s === 'active' || s === 'seasonal' || s === 'retired') return s
  if (s === 'eighty_sixed' || s === '86ed' || s === '86d') return 'eighty_sixed'
  return null
}

function parseSourceType(v: unknown): SourceType | null {
  const s = String(v ?? '').trim().toLowerCase().replace(/[\s-]+/g, '')
  if (s === 'inhouse') return 'inhouse'
  if (s === 'procured' || s === 'purchased' || s === 'bought') return 'procured'
  return null
}

// A worked example covering every supported column at once: a per-piece-only
// item, a tray-only item, an item sold in-store + catering + wholesale, an
// in-store-only item, and an accompaniment — with blanks left in various
// places so it's obvious which columns are optional.
function downloadTemplate() {
  const rows = [
    { Category: 'Breakfast', Item: 'Idly', 'Regional Name': '', Description: '', 'UPC Code': '', Price: 1.75, 'Wholesale Price': '', Status: 'active', 'Source Type': 'inhouse',
      'In-Store': 'yes', Catering: 'yes', 'Store Supply': 'no', Accompaniment: 'no',
      'Per Piece Price': 1.75, 'Min Pieces': 50,
      'Half Tray Price': '', 'Half Tray Serves': '', 'Half Tray Pieces': '',
      'Medium Tray Price': '', 'Medium Tray Serves': '', 'Medium Tray Pieces': '',
      'Large Tray Price': '', 'Large Tray Serves': '', 'Large Tray Pieces': '' },
    { Category: 'Breakfast', Item: 'Pongal', 'Regional Name': '', Description: '', 'UPC Code': '', Price: '', 'Wholesale Price': '', Status: 'active', 'Source Type': 'inhouse',
      'In-Store': 'no', Catering: 'yes', 'Store Supply': 'no', Accompaniment: 'no',
      'Per Piece Price': '', 'Min Pieces': '',
      'Half Tray Price': 50, 'Half Tray Serves': 15, 'Half Tray Pieces': '',
      'Medium Tray Price': 80, 'Medium Tray Serves': 30, 'Medium Tray Pieces': '',
      'Large Tray Price': 100, 'Large Tray Serves': 45, 'Large Tray Pieces': '' },
    { Category: 'Rice', Item: 'Veg Biryani', 'Regional Name': '', Description: 'Fragrant basmati rice with mixed vegetables', 'UPC Code': '860010396001', Price: 12.99, 'Wholesale Price': 8.00, Status: 'active', 'Source Type': 'inhouse',
      'In-Store': 'yes', Catering: 'yes', 'Store Supply': 'yes', Accompaniment: 'no',
      'Per Piece Price': '', 'Min Pieces': '',
      'Half Tray Price': 60, 'Half Tray Serves': 15, 'Half Tray Pieces': '',
      'Medium Tray Price': 100, 'Medium Tray Serves': 30, 'Medium Tray Pieces': '',
      'Large Tray Price': 140, 'Large Tray Serves': 45, 'Large Tray Pieces': '' },
    { Category: 'Snacks', Item: 'Samosa', 'Regional Name': '', Description: '', 'UPC Code': '', Price: 2.50, 'Wholesale Price': '', Status: 'active', 'Source Type': 'inhouse',
      'In-Store': 'yes', Catering: 'no', 'Store Supply': 'no', Accompaniment: 'no',
      'Per Piece Price': '', 'Min Pieces': '',
      'Half Tray Price': '', 'Half Tray Serves': '', 'Half Tray Pieces': '',
      'Medium Tray Price': '', 'Medium Tray Serves': '', 'Medium Tray Pieces': '',
      'Large Tray Price': '', 'Large Tray Serves': '', 'Large Tray Pieces': '' },
    { Category: 'Accompaniment', Item: 'Ginger Chutney', 'Regional Name': '', Description: '', 'UPC Code': '', Price: '', 'Wholesale Price': '', Status: 'active', 'Source Type': 'inhouse',
      'In-Store': 'no', Catering: 'yes', 'Store Supply': 'no', Accompaniment: 'yes',
      'Per Piece Price': '', 'Min Pieces': '',
      'Half Tray Price': 20, 'Half Tray Serves': 20, 'Half Tray Pieces': '',
      'Medium Tray Price': 35, 'Medium Tray Serves': 40, 'Medium Tray Pieces': '',
      'Large Tray Price': '', 'Large Tray Serves': '', 'Large Tray Pieces': '' },
  ]
  const ws = XLSX.utils.json_to_sheet(rows)
  ws['!cols'] = Object.keys(rows[0]).map(k => ({ wch: Math.max(10, k.length + 2) }))
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Products')
  XLSX.writeFile(wb, 'rezzops-product-import-template.xlsx')
}

type Step = 'upload' | 'map' | 'preview' | 'done'

export function ProductImportModal({ onClose }: Props) {
  const { data: existingItems = [] } = useMenuItems()
  const bulkImport = useBulkImportProducts()

  const [step, setStep] = useState<Step>('upload')
  const [fileName, setFileName] = useState('')
  const [headers, setHeaders] = useState<string[]>([])
  const [sheetRows, setSheetRows] = useState<Record<string, unknown>[]>([])
  const [mapping, setMapping] = useState<Record<string, TargetField>>({})
  const [excludedRows, setExcludedRows] = useState<Set<number>>(new Set())
  const [parseError, setParseError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const existingNameSet = useMemo(
    () => new Set(existingItems.map(i => i.name.trim().toLowerCase())),
    [existingItems]
  )

  async function handleFile(file: File) {
    setParseError(null)
    try {
      const buf = await file.arrayBuffer()
      const wb = XLSX.read(buf, { type: 'array' })
      const sheet = wb.Sheets[wb.SheetNames[0]]
      const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' })
      if (rows.length === 0) {
        setParseError('That file has no rows we could read.')
        return
      }
      const hdrs = Object.keys(rows[0])
      setHeaders(hdrs)
      setSheetRows(rows)
      setFileName(file.name)
      const guessed: Record<string, TargetField> = {}
      for (const h of hdrs) guessed[h] = guessField(h)
      setMapping(guessed)
      setExcludedRows(new Set())
      setStep('map')
    } catch {
      setParseError('Could not read that file. Make sure it’s a valid .xlsx, .xls, or .csv file.')
    }
  }

  // Rows normalized against the current column mapping — recomputed live as
  // the user adjusts dropdowns in the mapping step.
  const parsedRows: ImportProductRow[] = useMemo(() => {
    const fieldToHeader = new Map<TargetField, string>()
    for (const [header, field] of Object.entries(mapping)) {
      if (field !== 'skip') fieldToHeader.set(field, header)
    }
    const get = (row: Record<string, unknown>, field: TargetField) => {
      const h = fieldToHeader.get(field)
      return h ? row[h] : undefined
    }
    return sheetRows.map(row => ({
      category: String(get(row, 'category') ?? '').trim(),
      name: String(get(row, 'name') ?? '').trim(),
      regional_name: String(get(row, 'regional_name') ?? '').trim() || null,
      description: String(get(row, 'description') ?? '').trim() || null,
      upc_code: String(get(row, 'upc_code') ?? '').trim() || null,
      price: parseNumber(get(row, 'price')),
      wholesale_price: parseNumber(get(row, 'wholesale_price')),
      status: parseStatus(get(row, 'status')),
      source_type: parseSourceType(get(row, 'source_type')),
      available_in_store: parseBoolean(get(row, 'available_in_store')),
      available_catering: parseBoolean(get(row, 'available_catering')),
      available_store_supply: parseBoolean(get(row, 'available_store_supply')),
      is_addon: parseBoolean(get(row, 'is_addon')),
      per_piece_price: parseNumber(get(row, 'per_piece_price')),
      min_pieces: parseNumber(get(row, 'min_pieces')),
      half_tray_price: parseNumber(get(row, 'half_tray_price')),
      half_tray_serves: parseNumber(get(row, 'half_tray_serves')),
      half_tray_pieces: parseNumber(get(row, 'half_tray_pieces')),
      medium_tray_price: parseNumber(get(row, 'medium_tray_price')),
      medium_tray_serves: parseNumber(get(row, 'medium_tray_serves')),
      medium_tray_pieces: parseNumber(get(row, 'medium_tray_pieces')),
      full_tray_price: parseNumber(get(row, 'full_tray_price')),
      full_tray_serves: parseNumber(get(row, 'full_tray_serves')),
      full_tray_pieces: parseNumber(get(row, 'full_tray_pieces')),
    }))
  }, [sheetRows, mapping])

  const hasNameField = Object.values(mapping).includes('name')
  const hasCategoryField = Object.values(mapping).includes('category')

  const rowsToImport = parsedRows.filter((_, i) => !excludedRows.has(i))
  const newCategoryCount = useMemo(() => {
    return new Set(rowsToImport.map(r => r.category.toLowerCase()).filter(Boolean)).size
  }, [rowsToImport])

  function toggleRow(i: number) {
    setExcludedRows(prev => {
      const next = new Set(prev)
      if (next.has(i)) next.delete(i); else next.add(i)
      return next
    })
  }

  async function handleImport() {
    const valid = rowsToImport.filter(r => r.name)
    await bulkImport.mutateAsync(valid)
    setStep('done')
  }

  // Availability shown in the preview reflects explicit yes/no when mapped,
  // otherwise the same defaults the importer itself will apply.
  function effectiveAvailability(r: ImportProductRow) {
    const hasCatering = [r.per_piece_price, r.half_tray_price, r.medium_tray_price, r.full_tray_price].some(v => v != null)
    return {
      inStore: r.available_in_store ?? true,
      catering: r.available_catering ?? hasCatering,
      storeSupply: r.available_store_supply ?? false,
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto py-6">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl mx-4 flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200">
          <h2 className="text-base font-semibold text-stone-900">Import Products</h2>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-600">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto">
          {/* Step: upload */}
          {step === 'upload' && (
            <div className="space-y-4">
              <p className="text-sm text-stone-500">
                Upload an Excel (.xlsx/.xls) or CSV file with your product list. The first row should be
                column headers. Supports everything on the product form — name, pricing across all three
                channels, UPC, description, status, source type, and full catering tray/serving details.
                You'll map columns and review everything before anything is created.
              </p>
              <button
                type="button"
                onClick={downloadTemplate}
                className="flex items-center gap-1.5 text-xs font-medium text-brand-600 hover:text-brand-700"
              >
                <FileDown size={13} />
                Download a sample template (.xlsx) with every supported column filled in
              </button>
              <div
                onClick={() => fileInputRef.current?.click()}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleFile(f) }}
                className="border-2 border-dashed border-stone-300 rounded-xl p-10 text-center cursor-pointer hover:border-brand-400 hover:bg-brand-50/30 transition-colors"
              >
                <Upload size={28} className="mx-auto text-stone-400 mb-2" />
                <p className="text-sm font-medium text-stone-700">Click to choose a file, or drag one here</p>
                <p className="text-xs text-stone-400 mt-1">.xlsx, .xls, or .csv</p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f) }}
              />
              {parseError && <p className="text-sm text-danger">{parseError}</p>}
            </div>
          )}

          {/* Step: map columns */}
          {step === 'map' && (
            <div className="space-y-4">
              <p className="text-sm text-stone-500">
                <strong>{fileName}</strong> — {sheetRows.length} row{sheetRows.length === 1 ? '' : 's'} found.
                Confirm what each column means (we've guessed for you) — unmapped columns are simply ignored:
              </p>
              <div className="rounded border border-stone-200 divide-y divide-stone-100 max-h-96 overflow-y-auto">
                {headers.map(h => (
                  <div key={h} className="flex items-center justify-between gap-3 px-4 py-2.5">
                    <span className="text-sm font-medium text-stone-800 truncate">{h}</span>
                    <select
                      value={mapping[h] ?? 'skip'}
                      onChange={e => setMapping(m => ({ ...m, [h]: e.target.value as TargetField }))}
                      className="h-8 rounded border border-stone-300 bg-white px-2 text-xs text-stone-900 focus:outline-none focus:ring-2 focus:ring-brand-500 max-w-[220px]"
                    >
                      {FIELD_GROUPS.map(g => g.label ? (
                        <optgroup key={g.label} label={g.label}>
                          {g.fields.map(f => <option key={f} value={f}>{FIELD_LABELS[f]}</option>)}
                        </optgroup>
                      ) : (
                        g.fields.map(f => <option key={f} value={f}>{FIELD_LABELS[f]}</option>)
                      ))}
                    </select>
                  </div>
                ))}
              </div>
              {(!hasNameField || !hasCategoryField) && (
                <p className="text-xs text-warning-dark bg-warning-light rounded px-3 py-2">
                  Map at least one column to <strong>Item Name</strong> and one to <strong>Category</strong> to continue.
                </p>
              )}
              <div className="flex justify-between">
                <Button variant="outline" size="sm" onClick={() => setStep('upload')}>Back</Button>
                <Button size="sm" disabled={!hasNameField || !hasCategoryField} onClick={() => setStep('preview')}>
                  Preview {sheetRows.length} rows
                </Button>
              </div>
            </div>
          )}

          {/* Step: preview */}
          {step === 'preview' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-sm text-stone-600">
                  {rowsToImport.length} of {parsedRows.length} rows will be imported
                  {newCategoryCount > 0 && <> · {newCategoryCount} new categor{newCategoryCount === 1 ? 'y' : 'ies'} will be created</>}
                </p>
              </div>
              <div className="rounded border border-stone-200 overflow-x-auto max-h-96 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-stone-50 border-b border-stone-200">
                    <tr>
                      <th className="w-8 px-3 py-2" />
                      <th className="text-left px-3 py-2 font-semibold text-stone-500 uppercase tracking-wide">Category</th>
                      <th className="text-left px-3 py-2 font-semibold text-stone-500 uppercase tracking-wide">Item</th>
                      <th className="text-center px-2 py-2 font-semibold text-stone-500 uppercase tracking-wide">Avail.</th>
                      <th className="text-right px-3 py-2 font-semibold text-stone-500 uppercase tracking-wide">Price</th>
                      <th className="text-right px-3 py-2 font-semibold text-stone-500 uppercase tracking-wide">Pc</th>
                      <th className="text-right px-3 py-2 font-semibold text-stone-500 uppercase tracking-wide">Sm</th>
                      <th className="text-right px-3 py-2 font-semibold text-stone-500 uppercase tracking-wide">Med</th>
                      <th className="text-right px-3 py-2 font-semibold text-stone-500 uppercase tracking-wide">Lg</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {parsedRows.map((r, i) => {
                      const excluded = excludedRows.has(i)
                      const isDuplicate = r.name && existingNameSet.has(r.name.toLowerCase())
                      const missingName = !r.name
                      const avail = effectiveAvailability(r)
                      return (
                        <tr key={i} className={cn(excluded && 'opacity-40', (isDuplicate || missingName) && !excluded && 'bg-warning-light/40')}>
                          <td className="px-3 py-1.5">
                            <input type="checkbox" checked={!excluded} onChange={() => toggleRow(i)} />
                          </td>
                          <td className="px-3 py-1.5 text-stone-700">{r.category || <span className="text-danger">missing</span>}</td>
                          <td className="px-3 py-1.5 text-stone-900 font-medium">
                            {r.name || <span className="text-danger">missing name</span>}
                            {r.is_addon && <span className="ml-1.5 text-2xs text-stone-400">(accompaniment)</span>}
                            {isDuplicate && (
                              <span className="ml-1.5 inline-flex items-center gap-0.5 text-2xs text-warning-dark" title="An item with this name already exists">
                                <AlertTriangle size={10} /> exists
                              </span>
                            )}
                          </td>
                          <td className="px-2 py-1.5">
                            <div className="flex items-center justify-center gap-1">
                              <span title="In-Store" className={avail.inStore ? 'text-brand-500' : 'text-stone-200'}><Store size={12} /></span>
                              <span title="Catering" className={avail.catering ? 'text-brand-500' : 'text-stone-200'}><UtensilsCrossed size={12} /></span>
                              <span title="Store Supply" className={avail.storeSupply ? 'text-brand-500' : 'text-stone-200'}><ShoppingBag size={12} /></span>
                            </div>
                          </td>
                          <td className="px-3 py-1.5 text-right text-stone-600">{r.price != null ? `$${r.price.toFixed(2)}` : '—'}</td>
                          <td className="px-3 py-1.5 text-right text-stone-600">{r.per_piece_price != null ? `$${r.per_piece_price.toFixed(2)}` : '—'}</td>
                          <td className="px-3 py-1.5 text-right text-stone-600">{r.half_tray_price != null ? `$${r.half_tray_price.toFixed(2)}` : '—'}</td>
                          <td className="px-3 py-1.5 text-right text-stone-600">{r.medium_tray_price != null ? `$${r.medium_tray_price.toFixed(2)}` : '—'}</td>
                          <td className="px-3 py-1.5 text-right text-stone-600">{r.full_tray_price != null ? `$${r.full_tray_price.toFixed(2)}` : '—'}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-stone-400">
                Rows highlighted in yellow are missing a name/category or match an existing product name — uncheck any you don't want to import.
                UPC, description, serves, and pieces are imported but not shown here to keep this readable — check the mapping step if you need to confirm them.
              </p>
              {bulkImport.isError && (
                <p className="text-sm text-danger">
                  {bulkImport.error instanceof Error ? bulkImport.error.message : 'Import failed'}
                </p>
              )}
              <div className="flex justify-between">
                <Button variant="outline" size="sm" onClick={() => setStep('map')}>Back</Button>
                <Button size="sm" onClick={handleImport} loading={bulkImport.isPending} disabled={rowsToImport.filter(r => r.name).length === 0}>
                  {bulkImport.isPending ? <Loader2 size={13} className="animate-spin" /> : null}
                  Import {rowsToImport.filter(r => r.name).length} products
                </Button>
              </div>
            </div>
          )}

          {/* Step: done */}
          {step === 'done' && bulkImport.data && (
            <div className="text-center py-8 space-y-3">
              <div className="w-12 h-12 rounded-full bg-success-light flex items-center justify-center mx-auto">
                <Check size={22} className="text-success-dark" />
              </div>
              <p className="text-stone-900 font-medium">Import complete</p>
              <p className="text-sm text-stone-500">
                {bulkImport.data.itemsCreated} product{bulkImport.data.itemsCreated === 1 ? '' : 's'} created
                {bulkImport.data.categoriesCreated > 0 && <> · {bulkImport.data.categoriesCreated} new categor{bulkImport.data.categoriesCreated === 1 ? 'y' : 'ies'}</>}
                {bulkImport.data.cateringPricesSet > 0 && <> · {bulkImport.data.cateringPricesSet} with catering pricing</>}
              </p>
              <Button size="sm" onClick={onClose}>Done</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
